import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { toast } from '@/hooks/use-toast';
import {
  ContactSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { useProducerFormData } from '@/hooks/useProducerFormData';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';

export default function ProducerKontakt() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateProducerMutation = useUpdateProducer();
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);

  const isDemo = farmId === 'demo';
  const canEdit = isDemo || hasCapability('canEditProducerInfo');

  const { initialFormData } = useProducerFormData(
    producer,
    isDemo,
    farmId
  );

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (data) => {
      if (isDemo) {
        toast({ title: 'Sparat', description: 'Sparat (demo).' });
        return;
      }
      if (!producer) return;

      const requestId = generateRequestId();
      const payload = {
        phone: data.phone,
        email: data.email,
        website: data.website,
        instagram: data.instagram,
        facebook: data.facebook,
        opening_hours_text: data.openingHoursText || null,
        phone_public: data.phonePublic,
        email_public: data.emailPublic,
        website_public: data.websitePublic,
        instagram_public: data.instagramPublic,
        facebook_public: data.facebookPublic,
      };

      try {
        await updateProducerMutation.mutateAsync({
          id: producer.id,
          updates: payload,
        });
        toast({ title: 'Sparat', description: 'Dina kontaktuppgifter har sparats.' });
      } catch (err: any) {
        captureAppError(err, {
          feature: 'producer_kontakt',
          action: 'save',
          producer_id: producer.id,
          request_id: requestId,
        });
        logSystemEvent({
          level: 'error',
          event_key: 'producer_contact_save_failed',
          domain: 'producer',
          entity_type: 'producer',
          entity_id: producer.id,
          message: `Producer contact save failed: ${err?.message || 'unknown'}`,
          request_id: requestId,
          context: buildSystemEventContext({ feature: 'producer_kontakt', action: 'save', surface: 'producer_kontakt' }),
        });
        toast({
          title: 'Kunde inte spara',
          description: 'Kontrollera att du har behörighet och försök igen.',
          variant: 'destructive',
        });
      }
    },
  });

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Kontakt">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Kontakt">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Värdkontakt"
    >
      <ProducerSectionHeader
        title="Värdkontakt"
        description="Värdens kontaktuppgifter (producent)."
        onSave={canEdit ? editorState.save : undefined}
        isSaving={editorState.isSaving || updateProducerMutation.isPending}
        isDirty={editorState.isDirty}
        isReadOnly={!canEdit}
      />

      <p className="text-sm text-muted-foreground mb-6">
        Det här är värdens kontaktuppgifter (producent). De kan skilja sig från kontaktuppgifter för platsen.
      </p>

      <ContactSection
        formData={editorState.formData}
        updateField={editorState.updateField}
      />
    </ProducerLayout>
  );
}